import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, CheckCircle2, PlayCircle, FileText, ArrowLeft } from "lucide-react";
import { Nav, Footer } from "./Nav";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";

export function CoursePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, openAuth } = useAuth();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [done, setDone] = useState([]);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const res = await api.get(`/course/${id}`); setData(res.data); }
    catch (e) { setErr(e?.response?.data?.detail || "Course content coming soon."); }
  };
  useEffect(() => { if (!user) return; load(); /* eslint-disable-next-line */ }, [id, user]);

  if (!user) {
    return (<><Nav /><div className="player-wrap"><div className="locked-box"><Lock size={28} /><h2>Sign in to view this course</h2><button className="btn btn-primary" onClick={() => openAuth("login")}>Sign in</button></div></div><Footer /></>);
  }

  const buy = async () => {
    try { const { data: d } = await api.post("/checkout/session", { item_id: id, origin_url: window.location.origin }); if (d.url) window.location.href = d.url; }
    catch { toast.error("Could not start checkout."); }
  };

  const markComplete = async () => {
    const l = data.lessons[active];
    const nd = done.includes(l.id) ? done : [...done, l.id];
    setDone(nd);
    const percent = Math.round((nd.length / data.lessons.length) * 100);
    try { await api.post("/progress", { item_id: id, title: data.title, percent }); } catch {}
    toast.success(percent >= 100 ? "Course complete!" : "Lesson marked complete");
    if (active < data.lessons.length - 1) setActive(active + 1);
  };

  return (
    <>
      <Nav />
      {err ? (
        <div className="player-wrap"><div className="locked-box"><h2>{err}</h2><Link className="btn btn-primary" to="/courses">Browse courses</Link></div></div>
      ) : !data ? (
        <div className="player-wrap"><div className="locked-box">Loading…</div></div>
      ) : (
        <div className="player-wrap">
          <aside className="lesson-list">
            <Link className="back-link" to="/courses"><ArrowLeft size={16} /> Courses</Link>
            <h3 style={{ margin: ".3rem 0 .2rem" }}>{data.title}</h3>
            <p className="empty" style={{ marginBottom: ".8rem" }}>{data.intro}</p>
            {!data.unlocked && (
              <div style={{ background: "var(--sand)", borderRadius: 12, padding: ".9rem", marginBottom: ".8rem" }}>
                <div style={{ display: "flex", gap: ".4rem", alignItems: "center", color: "var(--terra)", fontWeight: 800 }}><Lock size={15} /> Locked</div>
                <p className="empty" style={{ margin: ".3rem 0 .6rem" }}>Purchase to unlock all lessons.</p>
                <button className="btn btn-primary block" onClick={buy}>Buy this course</button>
              </div>
            )}
            {data.lessons.map((l, i) => (
              <div key={l.id} className={`lesson-item${i === active ? " active" : ""}${done.includes(l.id) ? " done" : ""}`}
                onClick={() => (data.unlocked ? setActive(i) : null)}>
                <span className="num">{done.includes(l.id) ? <CheckCircle2 size={15} /> : i + 1}</span>
                <div><strong style={{ fontSize: ".92rem" }}>{l.title}</strong><small>{l.type === "video" ? <PlayCircle size={12} /> : <FileText size={12} />} {l.duration}{l.locked ? " · locked" : ""}</small></div>
              </div>
            ))}
          </aside>

          <main className="lesson-main">
            {data.unlocked ? (() => {
              const l = data.lessons[active];
              return (
                <>
                  <span className="eyebrow">LESSON {active + 1} OF {data.lessons.length}</span>
                  <h2 style={{ marginTop: ".2rem" }}>{l.title}</h2>
                  {l.type === "video" ? (
                    <iframe className="video" src={`https://www.youtube.com/embed/${l.youtube}`} title={l.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : (
                    <div className="reading">{l.body}</div>
                  )}
                  {l.type === "video" && <p className="muted" style={{ marginTop: ".8rem" }}>{l.body}</p>}
                  <div style={{ display: "flex", gap: ".6rem", marginTop: "1.2rem" }}>
                    <button className="btn btn-primary" onClick={markComplete}>{done.includes(l.id) ? "Completed ✓" : "Mark complete"}</button>
                    {active < data.lessons.length - 1 && <button className="btn btn-soft" onClick={() => setActive(active + 1)}>Next lesson</button>}
                  </div>
                </>
              );
            })() : (
              <div className="locked-box">
                <Lock size={30} />
                <h2>Unlock “{data.title}”</h2>
                <p className="muted">Preview the curriculum on the left. Purchase to start learning with full video and reading lessons.</p>
                <button className="btn btn-primary" onClick={buy}>Buy this course</button>
              </div>
            )}
          </main>
        </div>
      )}
      <Footer />
    </>
  );
}
